'use client'

import React, { useState, useEffect, useMemo } from 'react';

import Form, { FormGeneratorHandles } from '@/components/Form';
import Splash from "@/components/splash/Splash";
import Button from "@/components/UiElements/Button";
import { createUserFormItems } from "./forms/userFormItems";
import createUser from '@/services/UserService/createUser';
import getCompanyRoles from '@/services/UserService/getCompanyRoles';
import { Role } from '@/types/role';
import notifyError from '@/utils/notifyError';

interface CreateUserModalProps {
  onClose: () => void;
  onUserCreated: () => void;
}

interface RoleOption {
  label: string;
  value: string;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onUserCreated }) => {
  const formRef = React.useRef<FormGeneratorHandles>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  useEffect(() => {
    const loadRoles = async () => {
      setIsLoading(true);

      try {
        const result = await getCompanyRoles();
        const roles = Array.isArray(result) ? result : result?.roles || [];
        const options = roles.map((role: Role) => ({
          label: role.name,
          value: role.name
        }));
        setRoleOptions(options);
      } catch (error) {
        setRoleOptions([]);
        notifyError(error, 'Unable to load roles.');
      } finally {
        setIsLoading(false);
        setRolesLoaded(true);
      }
    };
    loadRoles();
  }, []);

  const formItems = useMemo(() => createUserFormItems(roleOptions), [roleOptions]);

  const handleCreateUser = async () => {
    const errors = await formRef.current?.validate();
    if (errors && errors.length > 0) {
      return;
    }

    const formData = formRef.current?.getData();
    if (!formData) return;

    setIsLoading(true);

    const payload = {
      email: formData.email,
      password: formData.password,
      roles: Array.isArray(formData.roles)
        ? formData.roles.map((r: any) => r?.value || r)
        : []
    };

    const result = await createUser(payload);
    setIsLoading(false);

    if (result) {
      onUserCreated();
      onClose();
    }
  };

  const handleCancel = () => {
    const formData = formRef.current?.getData();
    const hasData = formData && (
      (formData.email && formData.email.trim() !== '') ||
      (formData.password && formData.password.trim() !== '') ||
      (Array.isArray(formData.roles) && formData.roles.length > 0)
    );

    if (hasData) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
  };

  return (
    <div className={"flex flex-col gap-xxxl"}>
      {isLoading && <Splash />}
      <form className={"w-[500px] grid grid-cols-12 gap-md"} autoComplete="off" data-form-type="other" onSubmit={(e) => e.preventDefault()}>
        {/* Hidden fields to prevent browser password save prompt */}
        <input type="text" name="hidden_username" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
        <input type="password" name="hidden_password" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
        {rolesLoaded && (
          <Form
            ref={formRef}
            items={formItems}
          />
        )}
      </form>
      <div className={"flex flex-row justify-between gap-md"}>
        <Button
          onClick={handleCancel}
          type={"alt"}
          className={"flex-1 inset-1"}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateUser}
          className={"flex-1 inset-1"}
        >
          Create User
        </Button>
      </div>
    </div>
  );
};

export default CreateUserModal;
